export default {
  slug: "dallas-hyrox-simulation-results",
  metaTitle: "Dallas HYROX Simulation Results & Sim-Day Recaps",
  metaDescription:
    "Results and recaps from the Dallas HYROX simulations: how the leaderboard works, how to read your splits, and when the next sim-day recap publishes.",
  h1: "Dallas HYROX Simulation Results: Recaps and Leaderboards from Sim Day",
  category: "Simulations",
  pillar: false,
  datePublished: "2026-10-06",
  dateModified: "2026-10-06",
  readTime: 6,
  hero: {
    src: "/dps/poster-group.jpg",
    alt: "Group of athletes after a HYROX simulation at Dallas Performance Systems",
    caption: "The after photo every sim day earns.",
  },
  summary:
    "This is the results hub for the Dallas HYROX simulations run by Hyrox Ninja. After every sim day, finish times go up on the community leaderboard and a recap publishes here: division leaders, the field's spread, and what the data says about the next training block.",
  takeaways: [
    'Every Dallas sim time lives on the <a href="/results">community leaderboard</a>: singles and doubles, men\'s, women\'s and mixed.',
    "Recaps publish here within the week after each sim day.",
    "Your result is three numbers, not one: run total, station total, transition total.",
    "Posting your time is how the Dallas field gets a real local benchmark.",
  ],
  body: [
    { t: "p", html: "Race data is the whole point of simulating. This page is where the Dallas numbers live: how the board works, how to read your own result like a coach would, and the recap archive as sim days stack up." },

    { t: "h2", id: "how-board-works", text: "How the Dallas sim leaderboard works" },
    { t: "p", html: 'The <a href="/results">leaderboard</a> is community-reported: you race a Dallas sim, you post your finish time, it ranks instantly. Boards split by sim date, with division filters for singles and doubles and categories for men, women and mixed. Top three on each board get the highlight. One rule, socially enforced: post the real number.' },

    { t: "h2", id: "read-your-result", text: "How to read your sim result" },
    { t: "p", html: "A finish time alone hides the finding. Split your race into three buckets and compare:" },
    {
      t: "ul",
      items: [
        "<span><strong>Run total:</strong> all 8 kilometers. If your last three 1km splits are 60+ seconds slower than your first three, the engine is the next block.</span>",
        "<span><strong>Station total:</strong> all 8 stations. One outlier station eating 2+ minutes more than the others is a skill problem, which is the fastest kind to fix.</span>",
        "<span><strong>Transition total:</strong> everything else. More than 4 to 5 minutes here is free time you're donating.</span>",
      ],
    },
    { t: "p", html: 'Then feed the numbers into the pace calculator on the <a href="/simulation">simulation page</a> to project your race target. If you\'re new to all of this, the pillar guide to <a href="/blog/what-is-a-hyrox-simulation">what a HYROX simulation is</a> explains why this data transfers to race day.' },

    { t: "h2", id: "recaps", text: "Sim-day recaps" },
    { t: "p", html: "This section is wired straight into the live leaderboard. The moment times go up after a sim day, the recap below fills in automatically: finisher count, median time, fastest overall and the podium in every division." },
    { t: "live-recap" },
    { t: "p", html: 'The next full Dallas simulation is on the <a href="/events">events calendar</a> now. The narrative recap (what the data says about the field, and the pattern we keep seeing from coaching the floor — spoiler: it\'s almost always transitions) publishes here within the week after sim day.' },

    { t: "h2", id: "post-your-time", text: "How to post your time" },
    {
      t: "ol",
      items: [
        '<span>Open the <a href="/results">leaderboard</a> and find the post-your-time form.</span>',
        "<span>Enter your name or team, sim date, division (singles/doubles) and category (men/women/mixed).</span>",
        "<span>Enter your finish time as it was called, MM:SS or H:MM:SS. It ranks immediately.</span>",
      ],
    },

    { t: "h2", id: "weakest-split", text: "Train the split that lost" },
    { t: "p", html: "The board tells you where you rank; your three buckets tell you why. The most common Dallas finding is the run total fading, and the fix is specific: compromised running volume, not more station work." },
    { t: "disclosure" },
    { t: "p", html: 'For home run-volume on race-realistic equipment, a self-powered <a href="https://tidd.ly/4aT0HX3" rel="sponsored noopener" target="_blank">assault-style treadmill</a> is the closest thing to the real cost of running on tired legs. Everything else station-related is on the <a href="/gear">gear page</a>.' },

    { t: "h2", id: "find-a-sim", text: "Need a time to post first?" },
    { t: "p", html: 'Dates, waves and spot counts for upcoming Dallas sims are in the guide to <a href="/blog/hyrox-simulation-near-me-dallas">finding a HYROX simulation near you</a>, or go straight to the <a href="/events">events calendar</a>.' },
  ],
  faqs: [
    { q: "Who can post to the leaderboard?", a: "Anyone who finished a Hyrox Ninja Dallas simulation. The board is community-reported and community-policed: post the time that was called." },
    { q: "Are the results verified?", a: "Times are community-reported, but every sim is coach-timed on the floor, so the real number exists and the room knows it. The standard holds because the community holds it." },
    { q: "What's a good first simulation time?", a: "The honest range: most Dallas athletes finish between 60 and 120 minutes, and first-timers usually land in the back half of that. Your second sim versus your first is the number that matters." },
    { q: "When is the next simulation?", a: 'Live dates and spot counts are always on the <a href="/events">events page</a>. Sims land late in each 12-week training cycle.' },
  ],
  sources: [
    "Hyrox Ninja Dallas simulation leaderboard — hyroxninja.com/results",
    "Hyrox Ninja events calendar — hyroxninja.com/events",
  ],
  related: ["what-is-a-hyrox-simulation", "hyrox-simulation-near-me-dallas", "hyrox-simulation-workout"],
  cta: {
    heading: "Put your name on the board",
    html: "The Dallas field gets more honest every time someone posts. Race the sim, post the number, own the rank.",
    to: "/results",
    label: "Open the Leaderboard",
  },
};
